import { RouterClient } from "../../../../clients/routerClient";
import { Logger } from "../../../../clients/logger";
import { SpawnParams, TabCollection, WindowIdentifier } from "../types";
import { Launcher } from "../Launcher/launcher";
import { getStore } from "../store";
import { isHiddenTab, sendCommandToWindowService } from "../modules/main";
import { actions } from "../modules/actions";
import { ApiUsageTracker } from "../../common/apiUsageTracker";

/**
 * Brings windows to front or restores them from minimization. Works on all windows that are part of the workspace
 * unless a specific set of windows is passed in.
 */
const revealWindows = async ({ windowList }: { windowList?: string[] }) => {
	const state = getStore().getState();
	const windows =
		windowList ??
		Object.entries(state.main.launchData).flatMap(([windowName, entry]) => (entry.workspaceWindow ? [windowName] : []));
	const filtered = windows.filter((w) => !isHiddenTab(state, w));
	filtered.forEach((w) => {
		if (state.main.windows[w]?.physicalWindowState.windowState === "minimized") {
			sendCommandToWindowService(w, "restore");
		} else {
			sendCommandToWindowService(w, "bringToFront");
		}
	});
};

/**
 * Minimizes windows. Works on all windows that are part of the workspace
 * unless a specific set of windows is passed in.
 */
const minimizeWindows = async ({ windowList }: { windowList?: string[] }) => {
	const state = getStore().getState();
	const windows =
		windowList ??
		Object.entries(state.main.launchData).flatMap(([windowName, entry]) => (entry.workspaceWindow ? [windowName] : []));
	const filtered = windows.filter((w) => !isHiddenTab(state, w));
	filtered.forEach((w) => {
		sendCommandToWindowService(w, "minimize");
	});
};

/**
 * Updates tabCollections in the WindowStore to reflect the workspace that is
 * being loaded.
 */
const loadWorkspace = (tabCollections: TabCollection[]) => {
	getStore().dispatch(actions.loadWorkspace({ tabCollections }));
};

/**
 * Returns the windowName for a given router header. This is temporary
 * and should be replaced with an actual windowName in the header! It currently
 * extracts the window name from the origin entry (which is the router client name).
 * @param header - The router header.
 * @returns
 */
const windowNameFromRouterHeader = (header: any): string => {
	// Everything in between the first and last dots
	// RouterClient.Toolbar-1.dfdf-dfdfd-dfdf-dfdf => Toolbar-1
	let regex = /\.(.*)\./s;
	let regexMatch;
	try {
		regexMatch = header.origin.match(regex)[1];
	} catch (e) {
		// Fallback for native in the field. Everything after the first dot
		regexMatch = header.origin.split(".")[1];
	}
	return regexMatch;
};

export class LauncherEntry {
	launcher: Launcher;
	apiUsageTracker: ApiUsageTracker;

	moduleReady = false;

	constructor(launcher: Launcher) {
		this.launcher = launcher;
		this.apiUsageTracker = new ApiUsageTracker();
		this.bindAllFunctions();
		this.definePubicInterface();
	}

	setReady() {
		this.moduleReady = true;
	}

	bindAllFunctions() {
		const self = this;
		for (const name of Object.getOwnPropertyNames(Object.getPrototypeOf(self))) {
			if (self[name] instanceof Function) {
				self[name] = self[name].bind(self); // only bind function properties
			}
		}
	}

	definePubicInterface() {
		// Note: More Cleanup To Do: better to have all the router message handling done here, with pure calls into to the launcher (i.e. not passing in router message to launcher).
		// So for now using the launcher code close to as-is, although had to do some refactoring for this interface

		Logger.system.debug("LauncherEntry.definePubicInterface");

		RouterClient.addPubSubResponder("Launcher.WindowList", []);

		RouterClient.addResponder("Launcher.loadWorkspace", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			loadWorkspace(message?.data);
			message?.sendQueryResponse(null, "success");
		});

		RouterClient.addResponder("LauncherService.addWindowToGroups", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			this.launcher.addWindowToGroups(message?.data);
			message?.sendQueryResponse(null, "success");
		});

		RouterClient.addResponder("LauncherService.deleteWindowGroup", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			if (message) {
				this.launcher.deleteWindowGroup(err, message);
			} else {
				Logger.system.error("LauncherService.deleteWindowGroup addResponder failed");
			}
		});

		RouterClient.addResponder("Launcher.componentList", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			const components = this.launcher.getComponents();
			message?.sendQueryResponse(err, components);
		});

		RouterClient.addResponder("LauncherService.createWindowGroup", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			if (message) {
				this.launcher.createWindowGroup(err, message);
			} else {
				Logger.system.error("LauncherService.createWindowGroup addResponder failed");
			}
		});

		RouterClient.addResponder("LauncherService.getGroupsForWindow", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			message?.sendQueryResponse(null, this.launcher.getGroupsForWindow(message.data));
		});

		RouterClient.addResponder("Launcher.getActiveDescriptors", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			message?.sendQueryResponse(null, this.launcher.getActiveDescriptors());
		});

		RouterClient.addResponder("Launcher.getActiveAndPendingWindowNames", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			message?.sendQueryResponse(null, this.launcher.getActiveAndPendingWindowNames());
		});

		RouterClient.addResponder("Launcher.getPendingWindows", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			message?.sendQueryResponse(null, this.launcher.getPendingWindows());
		});

		RouterClient.addResponder("LauncherService.getComponentsThatCanReceiveDataTypes", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			message?.sendQueryResponse(null, this.launcher.getComponentsThatCanReceiveDataTypes(message.data.dataTypes));
		});

		RouterClient.addResponder("Launcher.getMonitorInfo", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			Logger.system.debug(`LauncherEntry.getMonitorInfo request${JSON.stringify(message)}`);
			this.launcher.getMonitorInfo(message?.data, (getMonitorInfoErr, response) => {
				message?.sendQueryResponse(getMonitorInfoErr, response);
			});
		});

		RouterClient.addResponder("Launcher.getMonitorInfoAll", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			Logger.system.debug(`LauncherEntry.getMonitorInfoAll request${JSON.stringify(message)}`);
			this.launcher.getMonitorInfoAll((getMonitorInfoAllErr, monitors) => {
				message?.sendQueryResponse(getMonitorInfoAllErr, monitors);
			});
		});

		RouterClient.addResponder("Launcher.removeComponent", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			this.launcher.remove(message?.data.name);
			message?.sendQueryResponse(err, message.data);
		});

		// imesner: 20220221: Citrix windows aren't controlled by finsemble, so the linker button uses
		// showWindowAtMouse to work across the client / server different system boundary
		RouterClient.addResponder("Launcher.showWindowAtMouse", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			Logger.system.debug(`LauncherEntry.showWindowAtMouse request${JSON.stringify(message)}`);
			this.launcher.showWindowAtMouse(message?.data.windowIdentifier, (showWindowErr, descriptor) => {
				message?.sendQueryResponse(showWindowErr, descriptor);
			});
		});

		RouterClient.addResponder("Launcher.showWindow", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			Logger.system.debug(`LauncherEntry.showWindow request${JSON.stringify(message)}`);
			this.launcher.showWindow(message?.data.windowIdentifier, message?.data, (showWindowErr, descriptor) => {
				message?.sendQueryResponse(showWindowErr, descriptor);
			});
		});

		RouterClient.addResponder("Launcher.spawn", async (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);

			if (message) {
				const { data: spawnParams }: { data: SpawnParams } = message;
				if (!spawnParams.options) spawnParams.options = {};
				if (!spawnParams.options.customData) spawnParams.options.customData = {};

				// @deprecated: Remove in 7.0
				spawnParams.options.execJSWhitelist = [windowNameFromRouterHeader(message.header)]; // <---- Susceptible to spoofing

				this.launcher.spawn(spawnParams, (error, descriptor) => {
					Logger.system.debug("LauncherEntry.Spawn done", message);
					message.sendQueryResponse(error, descriptor);
				});
			}
		});

		RouterClient.addResponder("Launcher.spawnTabCollection", async (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);

			if (message) {
				this.launcher.spawnTabCollection(message?.data, (error, descriptor) => {
					Logger.system.debug("LauncherEntry.spawnTabCollection done", message);
					message.sendQueryResponse(error, descriptor);
				});
			}
		});

		RouterClient.addResponder("Launcher.userDefinedComponentUpdate", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			function respond(error, response) {
				message?.sendQueryResponse(error, response);
			}
			if (message?.data.type === "add") {
				this.launcher.addUserDefinedComponent(message, respond);
			} else if (message?.data.type === "remove") {
				this.launcher.removeUserDefinedComponent(message, respond);
			}
		});

		RouterClient.addResponder("LauncherService.addWindowsToGroup", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			const errString = this.launcher.addWindowsToGroups(message?.data);
			if (errString) {
				message?.sendQueryResponse(errString);
			} else {
				message?.sendQueryResponse(null, "Success");
			}
		});

		RouterClient.addResponder("LauncherService.getWindowsInGroup", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			const windowList = this.launcher.getWindowsInGroup(message?.data.groupName);
			message?.sendQueryResponse(null, windowList);
		});

		RouterClient.addResponder("LauncherService.removeWindowsFromGroup", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			const errString = this.launcher.removeWindowsFromGroup(message?.data);
			if (errString) {
				message?.sendQueryResponse(errString);
			} else {
				message?.sendQueryResponse(null, "Success");
			}
		});

		RouterClient.addResponder("LauncherService.minimizeWindows", async (err, message) => {
			this.apiUsageTracker.track("LauncherService.minimizeWindows");
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			await minimizeWindows(message?.data);
			message?.sendQueryResponse(null);
		});
		RouterClient.addResponder("LauncherService.bringWindowsToFront", async (err, message) => {
			this.apiUsageTracker.track("LauncherService.bringWindowsToFront");
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			await revealWindows(message?.data);
			message?.sendQueryResponse(null);
		});
		// backward compatibility, just in case any client is calling this manually
		RouterClient.addResponder("WorkspaceService.minimizeAll", async (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			await minimizeWindows(message?.data);
			message?.sendQueryResponse(null);
		});
		// backward compatibility, just in case any client is calling this manually
		RouterClient.addResponder("WorkspaceService.bringWindowsToFront", async (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			await revealWindows(message?.data);
			message?.sendQueryResponse(null);
		});

		RouterClient.addResponder("LauncherService.registerComponent", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			if (message) {
				this.launcher.registerComponent(err, message);
			} else {
				Logger.system.error("LauncherService.registerComponent addResponder failed");
			}
		});

		RouterClient.addResponder("LauncherService.unRegisterComponent", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			if (message) {
				this.launcher.unRegisterComponent(err, message);
			} else {
				Logger.system.error("LauncherService.unRegisterComponent addResponder failed");
			}
		});

		RouterClient.addListener("Launcher.resetSpawnStagger", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			this.launcher.resetSpawnStagger(message?.data);
		});

		RouterClient.addListener("LauncherService.forceKillPendingWindows", (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);
			this.launcher.forceKillPendingWindows();
		});

		RouterClient.addResponder("Launcher.clone", async (err, message) => {
			this.moduleReady || Logger.system.error("windowService Launcher invoked before ready", message);

			if (message) {
				const { toClone, spawnParams = {} }: { toClone: WindowIdentifier; spawnParams: SpawnParams } = message.data;
				if (!spawnParams.options) spawnParams.options = {};
				if (!spawnParams.options.customData) spawnParams.options.customData = {};

				const { error, spawnParams: params } = await this.launcher.prepareCloneParams(toClone, spawnParams);

				if (error) {
					message.sendQueryResponse(error, { spawnParams: params });
				} else {
					this.launcher.spawn(params as SpawnParams, (err, descriptor) => {
						Logger.system.debug("LauncherEntry.Spawn done", message);
						message.sendQueryResponse(err, descriptor);
					});
				}
			}
		});
	}
}
